import React from 'react'
import 'react-loading-skeleton/dist/skeleton.css'

import { useAppSelector } from '../../../store/hooks'
import { ContractOfferTile } from '../../molecules'

type ContractOfferProps = {
  productReduceCharges?: boolean
}

const Component = ({ productReduceCharges }: ContractOfferProps) => {
  const customerInputValues = useAppSelector(state => state.customervalues)
  const contractState = useAppSelector(state => state.contract)

  const cloudToGoSelected = customerInputValues['p-intro']['intro-ctg-paketCloudToGo']
  const heatSelected = customerInputValues['p-intro']['intro-wae-waermePaket']
  const fafSelected = customerInputValues['p-intro']['intro-faf-paketFamilyAndFriends']
  const faf2Selected = customerInputValues['p-intro']['intro-faf-paketFamilyAndFriends'] &&
    customerInputValues['p-intro']['intro-faf-2-details']

  const anyAdditionalProductSelected = cloudToGoSelected || heatSelected || fafSelected
  const heatContractConsumption = customerInputValues['p-intro']['intro-wae-vorjahresstromverbrauch']
  const faf1ContractConsumption = customerInputValues['p-intro']['intro-faf-1-vorjahresstromverbrauch']
  const faf2ContractConsumption = customerInputValues['p-intro']['intro-faf-2-vorjahresstromverbrauch']
  function formatNumber (value: string, defaultValue = ''): string {
    const number = parseFloat(value)
    if (isNaN(number)) {
      return defaultValue
    } else {
      if (Number.isInteger(number)) {
        return number.toLocaleString('de-DE')
      } else {
        return number.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }
    }
  }
  function getDurationWithCorrectForm (durationInMonth: number, singularText: string, pluralText: string): string {
    const duration = Math.floor(Number(durationInMonth))
    return duration + (duration === 1 ? ` ${singularText}` : ` ${pluralText}`)
  }

  return (
    <div className="react-form__page react-form__page--tarif">
      <div className="react-form__page__header relative pt-10">
        <div className="text-center pb-10">
          <h2 className="h2">Ihre SENEC.Cloud</h2>
          {productReduceCharges
            ? <div className="warme-discount-wrapper flex flex-col md:flex-row pb-10 gap-4 md:gap-8">
                <div className="w-full md:w-1/2 px-4 md:px-8 flex items-center">
                  <p>Ihre SENEC.Cloud ist nur wenige Klicks entfernt.
                    Auf Basis Ihrer Angaben unterbreiten wir Ihnen das folgende Angebot.</p>
                </div>
                <div className="w-full sm:w-full md:w-1/2">
                  <div className="bg-theme-focus text-white rounded-full py-4 px-16">
                  <h5 className="h5 font-bold px-3 pt-2">Spart Geld: Cloud und Speicher!</h5>
                  <p>Nutzen Sie die Vorteile unserer steuerbaren Speicher P4 und E4
                  und sichern Sie sich bis zu {contractState.baseContract?.creditFromReducedGridFee} € pro Jahr.**</p>
                  <qz-link href=" https://senec.com/de/produkte/Chancen-%C2%A7-14a-EnWG#:~:text=Mit%20dem%20%C2%A7%2014a%20des,Last%20im%20Stromnetz%20zu%20optimieren"
                          button=""
                          outline=""
                          type="primary-ghost" target="_blank">
                    Mehr erfahren.
                  </qz-link>
                  <p className="text-xs mt-2">**Voraussetzung für die Reduzierung der Netzentgelte ist eine steuerbare Verbrauchseinrichtung nach § 14a EnWG und gemäß AGB Punkt 11.</p>
                  </div>
                </div>
              </div>
            : <p>Ihre SENEC.Cloud ist nur wenige Klicks entfernt. <br/>Auf Basis Ihrer Angaben unterbreiten wir Ihnen das
              folgende Angebot.</p>
          }
        </div>
        <ContractOfferTile
          productReduceCharges={productReduceCharges}
          productType="Cloud"
          downPayment={contractState.baseContract?.downPaymentInEuroPerMonth}
          bullet1="100% Ökostrom"
          info1="100 % Ökostrom aus Europa (Quellen: Sonne, Wind, Wasser oder Biomasse).
        Die SENEC erwirbt für meinen Jahresverbrauch sogenannte Herkunftsnachweise von
        einer regenerativen Erzeugungsanlage aus Europa."
          bullet2={
            getDurationWithCorrectForm(
              Number(contractState.baseContract.fixedPriceDurationInMonth),
              'Monat Preisgarantie',
              'Monate Preisgarantie'
            )
          }
          info2={'Ihr Strompreis bleibt ab Lieferbeginn ' +
            getDurationWithCorrectForm(
              Number(contractState.baseContract.fixedPriceDurationInMonth),
              'Monat',
              'Monate'
            ) +
            ' Monate unverändert - garantiert! ' +
            'Sie haben so die volle Kostenkontrolle und Gewissheit, ' +
            'dass Änderungen auf dem Strommarkt oder bei Steuern und Umlagen garantiert nicht an Sie ' +
            'weitergegeben werden.'}
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.baseContract.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Grundpreis pro Monat':
                formatNumber(contractState.baseContract.basePriceWithVatInEuroPerMonth) + ' €',
              'Verbrauchspreis pro kWh':
                formatNumber(contractState.baseContract.workingPriceWithVatInCtPerKWh) + ' Cent',
              'Erwarteter Jahresverbrauch':
                formatNumber(contractState.baseContract.calculatedGridImportInKwhPerYear) + ' kWh'
            }
          }
        />
        </div>
      {anyAdditionalProductSelected && (
        <div className="react-form__page__intro-text text-center p-10 py-10">
          <h2 className="h2">Ihre SENEC.Cloud Zusatzprodukte</h2>
        </div>
      )}
      {cloudToGoSelected && (
        <ContractOfferTile
          productType="Cloud to go"
          downPayment={contractState.ctgContract?.ctgTotalCostsMinusRenumerartionWithoutVatInEuroPerMonthRounded}
          bullet1="Größtes Ladenetz Europas"
          info1="Nutzen Sie Ihr Stromguthaben im EnBW Hypernetz, dem größten Ladenetz Deutschlands sowie in vielen Ländern Europas."
          bullet2="EnBW mobility+ App & Ladekarte"
          info2="Mit der EnBW mobility+ App haben Sie Zugriff auf das gesamte EnBW Hypernetz und können Ihre Ladevorgänge einfach und bequem steuern."
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.ctgContract?.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Jährliche Fahrleistung':
                formatNumber(customerInputValues['p-intro']['intro-ctg-annual-mileage']) + ' km',
              'Erwartete Lademenge':
                formatNumber(contractState.ctgContract?.totalEnergyAmount) + ' kWh',
              'Erwartete Ladekosten pro Jahr':
                formatNumber(contractState.ctgContract?.totalPriceWithVatInEuroPerYear) + ' €'
            }
          }
        />
      )}
      {fafSelected && (
        <ContractOfferTile
          productType="Family & Friends 1"
          downPayment={contractState.faf1Contract?.downPaymentInEuroPerMonth}
          bullet1="100% Ökostrom"
          info1="100 % Ökostrom aus Europa (Quellen: Sonne, Wind, Wasser oder Biomasse).
        Die SENEC erwirbt für meinen Jahresverbrauch sogenannte Herkunftsnachweise von
        einer regenerativen Erzeugungsanlage aus Europa."
          bullet2={
            getDurationWithCorrectForm(
              Number(contractState.faf1Contract?.fixedPriceDurationInMonth),
              'Monat Preisgarantie',
              'Monate Preisgarantie'
            )
          }
          info2={'Ihr Strompreis bleibt ab Lieferbeginn ' +
            getDurationWithCorrectForm(
              Number(contractState.faf1Contract?.fixedPriceDurationInMonth),
              'Monat',
              'Monate'
            ) + 'unverändert - garantiert! ' +
            'Sie haben so die volle Kostenkontrolle und Gewissheit,' +
            'dass Änderungen auf dem Strommarkt oder bei Steuern und Umlagen garantiert nicht an Sie ' +
            'weitergegeben werden.'}
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.faf1Contract?.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Grundpreis pro Monat':
                formatNumber(contractState.faf1Contract?.basePriceWithVatInEuroPerMonth) + ' €',
              'Verbrauchspreis pro kWh':
                formatNumber(contractState.faf1Contract?.workingPriceWithVatInCtPerKWh) + ' Cent',
              'Erwarteter Jahresverbrauch':
                formatNumber(faf1ContractConsumption) + ' kWh'
            }
          }
        />
      )}
      {faf2Selected && (
        <ContractOfferTile
          productType="Family & Friends 2"
          downPayment={contractState.faf2Contract?.downPaymentInEuroPerMonth}
          bullet1="100% Ökostrom"
          info1="100 % Ökostrom aus Europa (Quellen: Sonne, Wind, Wasser oder Biomasse).
        Die SENEC erwirbt für meinen Jahresverbrauch sogenannte Herkunftsnachweise von
        einer regenerativen Erzeugungsanlage aus Europa."
          bullet2={
            getDurationWithCorrectForm(
              Number(contractState.faf2Contract?.fixedPriceDurationInMonth),
              'Monat Preisgarantie',
              'Monate Preisgarantie'
            )
          }
          info2={'Ihr Strompreis bleibt ab Lieferbeginn ' +
            getDurationWithCorrectForm(
              Number(contractState.faf2Contract?.fixedPriceDurationInMonth),
              'Monat',
              'Monate'
            ) +
            ' Monate unverändert - garantiert! ' +
            'Sie haben so die volle Kostenkontrolle und Gewissheit,' +
            'dass Änderungen auf dem Strommarkt oder bei Steuern und Umlagen garantiert nicht an Sie ' +
            'weitergegeben werden.'}
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.faf2Contract?.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Grundpreis pro Monat':
                formatNumber(contractState.faf2Contract?.basePriceWithVatInEuroPerMonth) + ' €',
              'Verbrauchspreis pro kWh':
                formatNumber(contractState.faf2Contract?.workingPriceWithVatInCtPerKWh) + ' Cent',
              'Erwarteter Jahresverbrauch':
                formatNumber(faf2ContractConsumption) + ' kWh'
            }
          }
        />
      )}
      {heatSelected && productReduceCharges && (
        <ContractOfferTile
          productReduceCharges={productReduceCharges}
          productType="Wärme"
          downPayment={contractState.heatContract?.downPaymentInEuroPerMonth}
          bullet1="100% Ökostrom"
          info1="100 % Ökostrom aus Europa (Quellen: Sonne, Wind, Wasser oder Biomasse).
        Die SENEC erwirbt für meinen Jahresverbrauch sogenannte Herkunftsnachweise von
        einer regenerativen Erzeugungsanlage aus Europa."
          bullet2={
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.fixedPriceDurationInMonth),
              'Monat Preisgarantie',
              'Monate Preisgarantie'
            )
          }
          info2={'Ihr Strompreis bleibt ab Lieferbeginn ' +
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.fixedPriceDurationInMonth),
              'Monat',
              'Monate'
            ) +
            ' Monate unverändert - garantiert! ' +
            'Sie haben so die volle Kostenkontrolle und Gewissheit,' +
            'dass Änderungen auf dem Strommarkt oder bei Steuern und Umlagen garantiert nicht an Sie ' +
            'weitergegeben werden.'}
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Grundpreis pro Monat':
                formatNumber(contractState.heatContract?.basePriceWithVatInEuroPerMonth) + ' €',
              'Verbrauchspreis pro kWh':
                formatNumber(contractState.heatContract?.workingPriceWithVatInCtPerKWh) + ' Cent',
              'Erwarteter Jahresverbrauch':
                formatNumber(heatContractConsumption) + ' kWh'
            }
          }
        />
      )}
      {heatSelected && !productReduceCharges && (
        <ContractOfferTile
          productType="Wärme"
          downPayment={contractState.heatContract?.downPaymentInEuroPerMonth}
          bullet1="100% Ökostrom"
          info1="100 % Ökostrom aus Europa (Quellen: Sonne, Wind, Wasser oder Biomasse).
        Die SENEC erwirbt für meinen Jahresverbrauch sogenannte Herkunftsnachweise von
        einer regenerativen Erzeugungsanlage aus Europa."
          bullet2={
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.fixedPriceDurationInMonth),
              'Monat Preisgarantie',
              'Monate Preisgarantie'
            )
          }
          info2={'Ihr Strompreis bleibt ab Lieferbeginn ' +
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.fixedPriceDurationInMonth),
              'Monat',
              'Monate'
            ) +
            ' Monate unverändert - garantiert! ' +
            'Sie haben so die volle Kostenkontrolle und Gewissheit,' +
            'dass Änderungen auf dem Strommarkt oder bei Steuern und Umlagen garantiert nicht an Sie ' +
            'weitergegeben werden.'}
          bullet3={
            getDurationWithCorrectForm(
              Number(contractState.heatContract?.contractDurationInMonth),
              'Monat Mindestvertragslaufzeit',
              'Monate Mindestvertragslaufzeit'
            )
          }
          conditions={
            {
              'Grundpreis pro Monat':
                formatNumber(contractState.heatContract?.basePriceWithVatInEuroPerMonth) + ' €',
              'Verbrauchspreis pro kWh':
                formatNumber(contractState.heatContract?.workingPriceWithVatInCtPerKWh) + ' Cent',
              'Erwarteter Jahresverbrauch':
                formatNumber(heatContractConsumption) + ' kWh'
            }
          }
        />
      )}
    </div>
  )
}

export default Component
